import React from 'react';
import { NumberInput, Radio, RadioGroup, Select, Text } from '@mantine/core';
import { useSelector, useDispatch } from 'react-redux';
import NumberFormat from 'react-number-format';
import { calcActions, selectProperty } from '../../redux/calcSlice';
import styles from './Factory.module.scss';

type Props = {
  property: string,
  renderer?: string,
  decimals?: number,
  prefix?: string,
}

function getComponentName(property: any): string {
  if (property.component) {
    return property.component
  }

  if (property.options) {
    return "Select"
  }

  return "NumberInput"
}

function Factory(props: Props) {

  const dispatch = useDispatch()
  const property = useSelector(selectProperty(props.property))
  const component = getComponentName(property)
  
  switch (props.renderer || component) {
    case "NumberInput":
      return (
        <div className={styles.inputLine}>        
          <div className={styles.inputLabel}>
            {property.label}
          </div>
          <div className={styles.inputFieldContainer}>
            <NumberInput
              value={property.value}
              min={property.min}
              max={property.max}
              precision={property.precision}
              step={property.step}
              onChange={value => dispatch(calcActions.setValue({ name: props.property, value }))}
              className={property.unit ?styles.inputFieldWithUnit :styles.inputField}
            />
            {property.unit &&
              <div className={styles.inputUnit}>
                {property.unit}
              </div>
            }
          </div>
        </div>
      )

    case "NumberInputRight":
      return (
        <div className={styles.inputLineRight}>
{/*           <div className={styles.inputLabelRight}>
            {property.label}
          </div> */}
          <div className={styles.inputFieldContainerRight}>
            <NumberInput
              value={property.value}
              min={property.min}
              max={property.max}
              precision={property.precision}
              step={property.step}
              onChange={value => dispatch(calcActions.setValue({ name: props.property, value }))}
              className={property.unit ?styles.inputFieldWithUnit :styles.inputField}
            />
            {property.unit &&
              <div className={styles.inputUnit}>
                {property.unit}
              </div>
            }
          </div>
        </div>
      )

    case "Select":
      return (
        <div className={styles.inputLine}>        
          <div className={styles.inputLabel}>
            {property.label}
          </div>
          <div className={styles.inputFieldContainer}>
            <Select
              /* Note that Select value should always be either string or null */
              value={property.value}
              data={property.options}
              placeholder={property.placeholder}
              onChange={value => dispatch(calcActions.setValue({ name: props.property, value }))}
              className={styles.inputSelect}
            />
          </div>
        </div>
      )

    case "Radio":
      return (
        <div className={styles.inputLine}>        
          <div className={styles.inputLabel}>
            {property.label}
          </div>
          <div className={styles.inputFieldContainer}>
            <RadioGroup value={property.value} onChange={(event:any) => dispatch(calcActions.setValue({ name: props.property, value:event }))} color="dark">
              {
                property.options.map( (item:any, index:any) => {
                  return (<Radio value={item.value} key={index} className={styles.inputRadio} >{item.label}</Radio>)
                })
              }
            </RadioGroup>
          </div>
        </div>
      )

    case "Modal":
      
      if(component === 'NumberInput') {
        return (
          <div className={styles.inputLineModal}>        
            <div className={styles.inputLabelModal}>
              {property.label}
            </div>
            <div className={styles.inputFieldContainerModal}>
              <NumberInput
                value={property.value}
                min={property.min}
                max={property.max}
                precision={property.precision}
                step={property.step}
                onChange={value => dispatch(calcActions.setValue({ name: props.property, value }))}
                className={property.unit ?styles.inputFieldWithUnit :styles.inputField}
              />
              {property.unit &&
                <div className={styles.inputUnit}>
                  {property.unit}
                </div>
              }
            </div>
          </div>
        )
      }

      return (
        <div className={styles.inputLineModal}>        
          <div className={styles.inputLabelModal}>
            {property.label}
          </div>
          <div className={styles.inputFieldContainerModal}>
            <Select
              /* Note that Select value should always be either string or null */
              value={property.value}
              data={property.options}
              placeholder={property.placeholder}
              onChange={value => dispatch(calcActions.setValue({ name: props.property, value }))}
              className={styles.inputSelect}
            />
          </div>
        </div>
      )

    case "Result":
      return (
        <div className={styles.resultItem} >
          <div className={styles.resultContainerPadding} >
            <Text className={styles.resultValue} ><NumberFormat value={!property.value ?'-' :property.value } displayType={'text'} thousandsGroupStyle="thousand" thousandSeparator={' '} decimalScale={props.decimals!==undefined ?props.decimals :2}/></Text>
            <Text className={styles.resultUnit} >{property.unit}</Text>
          </div>
        </div>
      )

    case "ResultWithTitle":
      return (
        <div className={styles.resultItemWithTitle} >
          <Text className={styles.resultLabel} >{property.label}</Text>
          <div className={styles.resultContainer} >
            <Text className={styles.resultValue} ><NumberFormat value={!property.value ?'-' :property.value } displayType={'text'} thousandsGroupStyle="thousand" thousandSeparator={' '} decimalScale={props.decimals!==undefined ?props.decimals :2}/></Text>
            <Text className={styles.resultUnit} >{property.unit}</Text>
          </div>
        </div>
      )

    case "ResultLeft":
      return (
        <div className={styles.resultItemLeft} >
          <Text className={styles.resultLabel} >{property.label}</Text>
          <div className={styles.resultContainer} >
            <Text className={styles.resultValue} ><NumberFormat value={!property.value ?'-' :property.value } displayType={'text'} thousandsGroupStyle="thousand" thousandSeparator={' '} decimalScale={props.decimals!==undefined ?props.decimals :2}/></Text>
            <Text className={styles.resultUnit} >{property.unit}</Text>
          </div>
        </div>
      )

    case "MainInput":
      return (
        <div className={styles.resultItemNoBorder} >
          <div className={styles.resultContainer} >
            <Text className={styles.resultValue} >
              { component==='Select'
                ?<>{property.value}</>
                :<NumberFormat value={property.value} displayType={'text'} thousandsGroupStyle="thousand" thousandSeparator={' '} decimalScale={2}/>
              }              
            </Text>
            <Text className={styles.resultUnit} >{property.unit}</Text>
          </div>
        </div>
      )

    case "Benefits":
      return (
        <>
          <Text className={styles.benefitsValue} ><NumberFormat value={!property.value ?'-' :props.prefix === 'negation' ?(0-property.value) :property.value} displayType={'text'} thousandsGroupStyle="thousand" thousandSeparator={' '} decimalScale={0}/></Text>
          <Text className={styles.benefitsUnit} >{property.unit}</Text>
        </>
      )

    case "Table":
      return (
        <tr>
          <td>
            <Text className={styles.resultLabelTable} >{property.label}</Text>
          </td>
          <td style={{width:'250px'}}>
            <div className={styles.resultTableContainer} >
              <Text className={styles.resultValueTable} ><NumberFormat value={!property.value ?'-' :property.value } displayType={'text'} thousandsGroupStyle="thousand" thousandSeparator={' '} decimalScale={props.decimals!==undefined ?props.decimals :2} /></Text>
              <Text className={styles.resultUnitTable} >{property.unit}</Text>
            </div>
          </td>
        </tr>
      )

    default:
      return (
        <div>Suitable component not found.</div>
      )
  }
}

export default Factory;
